/**
 * Rows for the tour's drawer-peek bubble (CNCT-6771).
 *
 * design-contract.md → §Surface 3.2b (responsive: a step whose anchor is not rendered retargets to the
 * visible control that stands in for it).
 *
 * On a viewport where the left-nav rail is hidden, a step retargets to the hamburger — which tells the
 * user where to tap but not what to look for once they do. The peek answers that with a slice of the
 * menu: the row the step is about, plus its real neighbours faded either side.
 *
 * Rows are derived from the LIVE nav config rather than a table duplicated here, which matters for more
 * than tidiness: the tour's step titles and the nav rows' labels do not always agree (step 6 is titled
 * "Reporting"; the row reads "Reports"), and a hint whose whole job is "look for this" has to show the
 * label that is actually on the row. Sourcing it from the config means the two can never drift.
 */

import { navTourAnchor } from '@employer/app/services/nav-tour-anchor'

/** The shape this reads off a nav item. `IEmployerNavigationItem` satisfies it structurally. */
export interface INavRowSource {
  name?: string
  /** The row's label in the Favorites section, where it sits out of its group's context — NOT
   *  necessarily what the row itself renders. Company is "Company" on the row and "All Company
   *  Settings" in Favorites, so the peek cannot read this blindly. */
  secondaryName?: string
  /** Set on rows drawn by a custom template rather than from `name`. Messages is the one: it renders
   *  "Messages" while its item `name` is the singular "Message", so there `secondaryName` IS the
   *  rendered label. */
  customTemplateKey?: string
  icon?: string
  children?: unknown[]
  route?: string | any[]
  tourId?: string
}

/** One row as the peek renders it. */
export interface ITourPeekRow {
  label: string
  icon?: string
  /** Drives the chevron — a group row looks different from a leaf in the real rail. */
  hasChildren: boolean
  tourId: string
}

/** The peek's three slots. `above`/`below` are `null` at the ends of the menu. */
export interface ITourNavPeek {
  above: ITourPeekRow | null
  target: ITourPeekRow
  below: ITourPeekRow | null
}

/**
 * The Favorites section, as a peek row. It sits above every nav row in the menu but is NOT a nav config
 * item — it is its own component, built from the user's starred rows — so it has no entry to map from.
 * Callers prepend it only while the section is actually rendered (it hides itself when nothing is
 * starred).
 */
export const FAVORITES_PEEK_ROW: ITourPeekRow = { label: 'Favorites', icon: 'star', hasChildren: true, tourId: 'nav-favorites' }

/**
 * Map a nav item to a peek row, showing the label the row actually renders.
 *
 * `name` is that label for every row drawn from the config. `secondaryName` is only the rendered
 * label where a custom template draws the row — Messages — and otherwise means the row's name in the
 * Favorites section, which can differ: Company reads "Company" on the row and "All Company Settings"
 * in Favorites. Reading `secondaryName` first put the Favorites wording in a bubble whose whole job
 * is "look for this row".
 */
export function toPeekRow(item: INavRowSource): ITourPeekRow {
  return {
    label: (item.customTemplateKey ? item.secondaryName : undefined) ?? item.name ?? '',
    icon: item.icon,
    hasChildren: !!item.children?.length,
    tourId: navTourAnchor(item),
  }
}

/**
 * The target row and its immediate neighbours within `rows`, which must already be filtered to the rows
 * this user can actually see and ordered as the menu orders them — so the neighbours shown are the ones
 * the user will find. Returns `null` when the target is not among them (a gated or role-hidden area), so
 * the caller can fall back rather than name a row that is not there.
 */
export function buildNavPeek(rows: ITourPeekRow[], tourId: string): ITourNavPeek | null {
  const index = rows.findIndex(row => row.tourId === tourId)
  if (index === -1) return null
  return {
    above: rows[index - 1] ?? null,
    target: rows[index],
    below: rows[index + 1] ?? null,
  }
}
